import { useEffect, useState } from 'react';

/**
 * useFinePointer — is the doctor on a MOUSE (or trackpad), not a finger?
 *
 * The double-click shortcut (D41 / S17) exists only for a fine pointer: a
 * double-tap on a tablet is not a gesture this pad claims, and the hint that
 * teaches it must not appear where it cannot be used. The keyboard and touch
 * equivalent is `See all n`, which is always there — so nothing on the pad is
 * reachable ONLY by double-click.
 *
 * Re-read on change (a tablet with a keyboard case docks and undocks), one
 * listener, removed on unmount. `matchMedia` is guarded: jsdom and older
 * browsers report nothing, and "nothing" means coarse — the shortcut simply
 * does not arm.
 */
export default function useFinePointer() {
  const [fine, setFine] = useState(false);

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia?.('(pointer: fine)');
    } catch {
      return undefined;
    }
    if (!mq) return undefined;
    const read = () => setFine((prev) => (prev === mq.matches ? prev : mq.matches));
    read();
    // Safari < 14 only has the deprecated pair.
    if (mq.addEventListener) mq.addEventListener('change', read);
    else if (mq.addListener) mq.addListener(read);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', read);
      else if (mq.removeListener) mq.removeListener(read);
    };
  }, []);

  return fine;
}
